import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MessagingSystem from '../components/messaging/MessagingSystem';
import { ArrowRight, ChevronLeft, ChevronRight, Users, Star, Check, Zap, Shield, MessageCircle, Crown, Flame, RefreshCw, Camera, Loader } from 'lucide-react';
import { getVibeAvatar, getSavedAvatarStyle, saveAvatarStyle, AVATAR_STYLE_CATALOG } from '../data/avatarUtils';
import { VIBE_TYPE_AVATARS } from '../data/vibeAvatars';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Config (mirrors backend) ──────────────────────────────────────────────────

const VIBE_TYPE_META = {
  social_anchor:      { emoji: '🏠', label: 'The Social Anchor',      color: '#F59E0B' },
  quiet_professional: { emoji: '📚', label: 'The Quiet Professional',  color: '#6366F1' },
  night_owl:          { emoji: '🌙', label: 'The Night Owl',           color: '#94A3B8' },
  early_riser:        { emoji: '☀️', label: 'The Early Riser',         color: '#F97316' },
  free_spirit:        { emoji: '🌿', label: 'The Free Spirit',         color: '#10B981' },
  homebody:           { emoji: '🛋️', label: 'The Homebody',            color: '#8B5CF6' },
};

const TIER_STYLES = {
  excellent: { border: '#F59E0B', label: 'Excellent match', bg: 'rgba(245,158,11,0.15)' },
  strong:    { border: '#2cbdc4', label: 'Strong match',    bg: 'rgba(44,189,196,0.15)' },
  good:      { border: '#6366F1', label: 'Good match',      bg: 'rgba(99,102,241,0.1)'  },
  low:       { border: '#475569', label: 'Worth a look',    bg: 'rgba(71,85,105,0.1)'   },
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const GradBtn = ({ onClick, children, className = '', disabled = false }) => (
  <button onClick={onClick} disabled={disabled}
    className={`text-white font-semibold rounded-xl transition-opacity hover:opacity-90 disabled:opacity-40 ${className}`}
    style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
    {children}
  </button>
);

const CompatMeter = ({ score, size = 56 }) => {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const tier = score >= 85 ? 'excellent' : score >= 70 ? 'strong' : score >= 55 ? 'good' : 'low';
  const color = TIER_STYLES[tier].border;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={4} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <span className="absolute text-xs font-black text-white">{score}%</span>
    </div>
  );
};

const SliderField = ({ label, value, onChange, leftLabel, rightLabel }) => (
  <div className="mb-5">
    <div className="flex justify-between text-sm text-gray-600 mb-2">
      <span className="font-medium text-gray-800">{label}</span>
      <span className="text-xs text-gray-400">{leftLabel} ← → {rightLabel}</span>
    </div>
    <input type="range" min={1} max={10} value={value}
      onChange={e => onChange(parseInt(e.target.value))}
      className="w-full h-2 rounded-full accent-[#094f6f] cursor-pointer" />
    <div className="flex justify-between text-[10px] text-gray-400 mt-1">
      <span>{leftLabel}</span><span>{rightLabel}</span>
    </div>
  </div>
);

const PillSelect = ({ options, value, onChange, multi = false }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(opt => {
      const isSelected = multi ? (value || []).includes(opt.value) : value === opt.value;
      return (
        <button key={opt.value} type="button" onClick={() => {
          if (multi) {
            const cur = value || [];
            onChange(isSelected ? cur.filter(v => v !== opt.value) : [...cur, opt.value]);
          } else onChange(opt.value);
        }}
          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
            isSelected ? 'bg-[#094f6f] text-white border-[#094f6f]' : 'border-gray-200 text-gray-600 hover:border-[#094f6f]'
          }`}>
          {opt.label}
        </button>
      );
    })}
  </div>
);

// ── VibeMate Plus upgrade modal ────────────────────────────────────────────────

function PlusUpgradeModal({ onClose, token }) {
  const [loading, setLoading] = useState(false);

  const subscribe = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/vibemate/plus/subscribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-7 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            <Crown className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-black text-gray-900">VibeMate Plus</h2>
          <p className="text-3xl font-black mt-1" style={{ color: '#094f6f' }}>A$9.99<span className="text-base font-normal text-gray-400">/month</span></p>
        </div>
        <div className="space-y-3 mb-6">
          {[
            ['Unlimited profiles per day', '20/day on free'],
            ['See who liked your profile', 'Free: count only'],
            ['10 Super Vibes per day', '3/day on free'],
            ['Boost your profile once a week', 'Not available free'],
            ['Advanced filters by Vibe Type', 'Not available free'],
            ['Group formation unlocked', 'Plus only'],
          ].map(([feat, note]) => (
            <div key={feat} className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#2cbdc4' }}>
                <Check className="w-3 h-3 text-white" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-800">{feat}</span>
                <span className="text-xs text-gray-400 ml-2">{note}</span>
              </div>
            </div>
          ))}
        </div>
        <GradBtn onClick={subscribe} disabled={loading} className="w-full py-4 text-base flex items-center justify-center gap-2">
          <Crown className="w-4 h-4" />
          {loading ? 'Redirecting to payment...' : 'Upgrade to Plus'}
        </GradBtn>
        <button onClick={onClose} className="w-full text-center text-sm text-gray-400 mt-3 hover:text-gray-600">Maybe later</button>
      </div>
    </div>
  );
}

// ── Likes view (who swiped right on you) ──────────────────────────────────────

function LikesView({ token, isPlus, onUpgrade }) {
  const [data, setData] = useState({ count: 0, profiles: [], is_plus: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/vibemate/likes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="text-center py-20 text-gray-400">Loading...</div>;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-1">{data.count} {data.count === 1 ? 'person' : 'people'} liked you</h2>
      <p className="text-sm text-gray-400 mb-6">They swiped right on your profile</p>

      {!data.is_plus ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 text-3xl">
            {data.count > 0 ? '💌' : '🔒'}
          </div>
          {data.count > 0 ? (
            <>
              <p className="text-lg font-bold text-gray-800 mb-2">{data.count} {data.count === 1 ? 'person' : 'people'} already liked you</p>
              <p className="text-gray-500 text-sm mb-6">Upgrade to VibeMate Plus to see who they are and match instantly.</p>
              <GradBtn onClick={onUpgrade} className="px-6 py-3 text-sm flex items-center gap-2 mx-auto">
                <Crown className="w-4 h-4" /> Unlock with Plus
              </GradBtn>
            </>
          ) : (
            <p className="text-gray-400 text-sm">Once people like your profile, they'll appear here with Plus.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {data.profiles.map((p, i) => {
            const vt = VIBE_TYPE_META[p.vibe_type] || VIBE_TYPE_META.free_spirit;
            return (
              <div key={i} className="flex items-center gap-4 bg-white rounded-2xl border border-gray-100 p-4">
                <div className="w-12 h-12 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0"
                  style={{ background: `${vt.color}15` }}>
                  {p.photo_url
                    ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                    : <img src={VIBE_TYPE_AVATARS[p.vibe_type] || getVibeAvatar(p.display_name || p.full_name, p.vibe_type, null, 80)}
                        alt="" className="w-full h-full object-contain" />}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">{p.display_name || p.full_name}</p>
                  <p className="text-xs text-gray-400">{p.university || p.industry}</p>
                </div>
                {p.direction === 'super' && <span className="text-yellow-500 text-lg">⚡</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── University verification section ───────────────────────────────────────────

function UniVerification({ token, profile, onVerified }) {
  const [uniEmail, setUniEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(profile?.university_email_verified ? 'done' : 'input');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendCode = async () => {
    if (!uniEmail.includes('.edu.au') && !uniEmail.includes('unimelb') && !uniEmail.includes('unsw') && !uniEmail.includes('monash')) {
      setError('Please enter a valid Australian university email (.edu.au)');
      return;
    }
    setLoading(true); setError('');
    const r = await fetch(`${API}/api/vibemate/verify-university/send`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ university_email: uniEmail }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.detail || 'Error sending code'); }
    else setStep('code');
    setLoading(false);
  };

  const confirmCode = async () => {
    setLoading(true); setError('');
    const r = await fetch(`${API}/api/vibemate/verify-university/confirm`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ university_email: uniEmail, code }),
    });
    const d = await r.json();
    if (!r.ok) { setError(d.detail || 'Incorrect code'); }
    else { setStep('done'); if (onVerified) onVerified(); }
    setLoading(false);
  };

  if (step === 'done') return (
    <div className="flex items-center gap-3 bg-green-50 rounded-2xl p-4 border border-green-100">
      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
        <Check className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="font-semibold text-green-800 text-sm">Student Verified</p>
        <p className="text-xs text-green-600">{profile?.university_email}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-sky-50 rounded-2xl p-5 border border-sky-100">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-[#094f6f]" />
        <p className="font-semibold text-gray-800 text-sm">Verify Student Status</p>
      </div>
      <p className="text-xs text-gray-500 mb-4">Add your university email to get a verified badge on your profile.</p>
      {step === 'input' && (
        <>
          <input value={uniEmail} onChange={e => setUniEmail(e.target.value)}
            placeholder="your.name@university.edu.au"
            className="w-full border border-sky-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 mb-3" />
          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
          <GradBtn onClick={sendCode} disabled={loading} className="w-full py-2.5 text-sm">
            {loading ? 'Sending...' : 'Send Verification Code'}
          </GradBtn>
        </>
      )}
      {step === 'code' && (
        <>
          <p className="text-xs text-gray-500 mb-3">Check your university email for a 6-digit code.</p>
          <input value={code} onChange={e => setCode(e.target.value)} placeholder="000000" maxLength={6}
            className="w-full border border-sky-200 rounded-xl px-3 py-2.5 text-sm tracking-widest text-center mb-3 focus:outline-none" />
          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => setStep('input')} className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm text-gray-500">Back</button>
            <GradBtn onClick={confirmCode} disabled={loading} className="flex-1 py-2.5 text-sm">
              {loading ? 'Verifying...' : 'Verify'}
            </GradBtn>
          </div>
        </>
      )}
    </div>
  );
}

const VibeTypeReveal = ({ vibeType, onContinue }) => {
  const meta = VIBE_TYPE_META[vibeType] || VIBE_TYPE_META.free_spirit;
  const vt = vibeType || 'free_spirit';
  const avatarUrl = VIBE_TYPE_AVATARS[vt];
  return (
    <div className="text-center py-8">
      <p className="text-sm font-bold text-[#2cbdc4] uppercase tracking-widest mb-6">Your Vibe Type</p>
      <div className="w-40 h-40 rounded-3xl overflow-hidden mx-auto mb-6 shadow-2xl"
        style={{ border: `3px solid ${meta.color}60` }}>
        {avatarUrl
          ? <img src={avatarUrl} alt={meta.label} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center text-6xl"
              style={{ background: `linear-gradient(135deg,${meta.color}33,${meta.color}11)` }}>
              {meta.emoji}
            </div>
        }
      </div>
      <h2 className="text-4xl font-black text-gray-900 mb-3">{meta.label}</h2>
      <p className="text-gray-500 max-w-sm mx-auto mb-8 leading-relaxed">
        {vt === 'social_anchor' && "You make a house feel like a home. You cook, you chat, you bring people together. Your housemates will love you."}
        {vt === 'quiet_professional' && "Respectful, tidy, independent. You need space but you give it too. The dream housemate for anyone who values their own."}
        {vt === 'night_owl' && "You come alive after 10pm. You need housemates who respect late hours — and late mornings. Your best hours are theirs' quiet ones."}
        {vt === 'early_riser' && "Mornings are sacred. You run, you cook breakfast, you're productive before most people open their eyes. You're asleep by 10."}
        {vt === 'free_spirit' && "Flexible, easygoing, goes with the flow. You adapt to whoever you live with. You're the housemate everyone gets along with."}
        {vt === 'homebody' && "Home is your sanctuary. Quiet, cosy, deeply personal space. You don't need housemates to be your best friends — just respectful."}
      </p>
      <div className="bg-gray-50 rounded-2xl p-4 mb-8 max-w-sm mx-auto border border-gray-100">
        <p className="text-xs text-gray-400 mb-2">Share your Vibe Type</p>
        <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-gray-200 text-sm font-medium text-gray-600">
          <span>{meta.emoji}</span>
          <span>I'm {meta.label} on VibeMate — rentalx.io/vibemate</span>
        </div>
      </div>
      <GradBtn onClick={onContinue} className="px-10 py-4 text-base">
        See My Matches →
      </GradBtn>
    </div>
  );
};

// ── Onboarding flow ───────────────────────────────────────────────────────────

const AUSTRALIAN_UNIS = [
  'University of Melbourne','University of Sydney','UNSW Sydney','University of Queensland',
  'Monash University','RMIT University','University of Wollongong','University of Newcastle',
  'Australian National University','Curtin University','University of Western Australia',
  'University of Adelaide','Griffith University','Queensland University of Technology',
  'University of Technology Sydney','La Trobe University','Deakin University',
  'Macquarie University','University of South Australia','Bond University',
];

function Onboarding({ onComplete }) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({
    mode: 'student', display_name: '', age: '', gender: '',
    university: '', faculty: '', study_year: '', is_international: false,
    industry: '', job_title: '', work_location: '', work_arrangement: '', income_bracket: '',
    wake_time: 5, sleep_time: 5, noise_level: 5, home_frequency: 5,
    guests_frequency: 5, kitchen_habits: 5, cleanliness: 5,
    pet_preference: 'neutral', smoking: 'non_smoker',
    diet: 'no_restriction', language_preference: 'english_only', alcohol: 'fine',
    vibe_type: '', quiz_answers: [],
    prompt_answers: [], seeking: 'single_housemate',
    budget_min: 200, budget_max: 500, move_in_timeframe: 'flexible',
    min_lease: 'no_preference', preferred_suburbs: [],
    onboarding_step: 1, completed_onboarding: false,
  });
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [promptAnswers, setPromptAnswers] = useState({});
  const [vibeType, setVibeType] = useState('');
  const [showReveal, setShowReveal] = useState(false);
  const TOTAL_STEPS = 6;

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/vibemate/quiz-questions`).then(r => r.json()).then(d => setQuizQuestions(d.questions || []));
    fetch(`${API}/api/vibemate/prompts`).then(r => r.json()).then(d => setPrompts(d.prompts || []));
  }, []);

  const next = () => {
    if (step === 5 && !showReveal) {
      // Calculate vibe type from quiz answers
      const answers = quizQuestions.map((_, i) => quizAnswers[i] ?? 0);
      set('quiz_answers', answers);
      // Determine type client-side (mirrors backend logic)
      const counts = {};
      quizQuestions.forEach((q, qi) => {
        const ai = quizAnswers[qi] ?? 0;
        const opt = q.options?.[ai];
        if (opt?.votes) {
          Object.entries(opt.votes).forEach(([k, v]) => { counts[k] = (counts[k] || 0) + v; });
        }
      });
      const dominant = Object.entries(counts).sort(([,a],[,b]) => b - a)[0]?.[0] || 'free_spirit';
      setVibeType(dominant);
      set('vibe_type', dominant);
      setShowReveal(true);
      return;
    }
    if (step < TOTAL_STEPS) setStep(s => s + 1);
    else handleSubmit();
  };

  const handleSubmit = async () => {
    const finalPromptAnswers = selectedPrompts.map(idx => ({
      prompt_id: String(idx),
      question: prompts[idx] || '',
      answer: promptAnswers[idx] || '',
    })).filter(p => p.answer);
    const finalProfile = {
      ...profile,
      prompt_answers: finalPromptAnswers,
      completed_onboarding: true,
      onboarding_step: TOTAL_STEPS,
    };
    onComplete(finalProfile);
  };

  if (showReveal && step === 5) {
    return (
      <VibeTypeReveal vibeType={vibeType} onContinue={() => { setShowReveal(false); setStep(6); }} />
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress */}
      <div className="flex gap-1 mb-8">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i < step ? 'bg-[#094f6f]' : i === step - 1 ? 'bg-[#2cbdc4]' : 'bg-gray-200'}`} />
        ))}
      </div>

      {/* Step 1 — Who are you */}
      {step === 1 && (
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">Who are you?</h2>
          <p className="text-gray-500 mb-8">This shapes everything — your matches, your prompts, your feed.</p>

          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-3">I am a...</p>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: 'student', emoji: '🎓', l: 'Student', sub: 'Currently enrolled at uni' },
                { v: 'professional', emoji: '💼', l: 'Professional', sub: 'Working adult or grad' }].map(o => (
                <button key={o.v} onClick={() => set('mode', o.v)}
                  className={`p-5 rounded-2xl border-2 text-left transition-all ${profile.mode === o.v ? 'border-[#094f6f] bg-sky-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="text-3xl mb-2">{o.emoji}</div>
                  <p className="font-bold text-gray-900">{o.l}</p>
                  <p className="text-xs text-gray-500">{o.sub}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
              <input value={profile.display_name} onChange={e => set('display_name', e.target.value)}
                placeholder="e.g. Alex" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age *</label>
              <input type="number" value={profile.age} onChange={e => set('age', parseInt(e.target.value))}
                min={18} max={60} placeholder="22" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <PillSelect value={profile.gender} onChange={v => set('gender', v)} options={[
              { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' },
              { value: 'non_binary', label: 'Non-binary' }, { value: 'prefer_not', label: 'Prefer not to say' },
            ]} />
          </div>
        </div>
      )}

      {/* Step 2 — Your world */}
      {step === 2 && (
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">Your world</h2>
          <p className="text-gray-500 mb-8">Shapes how we match you with compatible housemates.</p>

          {profile.mode === 'student' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">University *</label>
                <select value={profile.university} onChange={e => set('university', e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30">
                  <option value="">Select your university</option>
                  {AUSTRALIAN_UNIS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Faculty / Course</label>
                  <input value={profile.faculty} onChange={e => set('faculty', e.target.value)}
                    placeholder="e.g. Engineering" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Study Year</label>
                  <select value={profile.study_year} onChange={e => set('study_year', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30">
                    <option value="">Select year</option>
                    {['1st Year','2nd Year','3rd Year','4th Year','Postgrad','PhD'].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Student type</p>
                <PillSelect value={profile.is_international ? 'international' : 'domestic'} onChange={v => set('is_international', v === 'international')}
                  options={[{ value: 'domestic', label: '🇦🇺 Domestic' }, { value: 'international', label: '🌏 International' }]} />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <select value={profile.industry} onChange={e => set('industry', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30">
                    <option value="">Select industry</option>
                    {['Technology','Finance','Healthcare','Education','Marketing','Engineering','Design','Law','Consulting','Hospitality','Other'].map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                  <input value={profile.job_title} onChange={e => set('job_title', e.target.value)}
                    placeholder="e.g. Software Engineer" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Work arrangement</p>
                <PillSelect value={profile.work_arrangement} onChange={v => set('work_arrangement', v)}
                  options={[{ value: 'office', label: '🏢 Full-time office' },{ value: 'hybrid', label: '🔄 Hybrid' },{ value: 'remote', label: '🏠 Remote' }]} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Income bracket (AUD/year)</p>
                <PillSelect value={profile.income_bracket} onChange={v => set('income_bracket', v)}
                  options={[{ value: 'under_60k', label: 'Under $60k' },{ value: '60_90k', label: '$60k–$90k' },{ value: '90_120k', label: '$90k–$120k' },{ value: '120k_plus', label: '$120k+' }]} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3 — Living rhythm */}
      {step === 3 && (
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">Your living rhythm</h2>
          <p className="text-gray-500 mb-8">This is the most important step. Be honest — it determines your best matches.</p>
          <SliderField label="Wake-up time" value={profile.wake_time} onChange={v => set('wake_time', v)} leftLabel="Before 7am ☀️" rightLabel="After noon 😴" />
          <SliderField label="Sleep time" value={profile.sleep_time} onChange={v => set('sleep_time', v)} leftLabel="Before 10pm 🌙" rightLabel="After midnight 🦉" />
          <SliderField label="Noise level at home" value={profile.noise_level} onChange={v => set('noise_level', v)} leftLabel="Silent sanctuary 🤫" rightLabel="Social & lively 🎶" />
          <SliderField label="Work from home frequency" value={profile.home_frequency} onChange={v => set('home_frequency', v)} leftLabel="Never 🏢" rightLabel="Every day 🏠" />
          <SliderField label="Guests & visitors" value={profile.guests_frequency} onChange={v => set('guests_frequency', v)} leftLabel="Never 🚪" rightLabel="Love hosting 🎉" />
          <SliderField label="Kitchen habits" value={profile.kitchen_habits} onChange={v => set('kitchen_habits', v)} leftLabel="Cook daily 👨‍🍳" rightLabel="Takeaway always 🍕" />
          <SliderField label="Cleanliness standard" value={profile.cleanliness} onChange={v => set('cleanliness', v)} leftLabel="Spotless ✨" rightLabel="Relaxed 😌" />
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">Pets</p>
            <PillSelect value={profile.pet_preference} onChange={v => set('pet_preference', v)}
              options={[{ value: 'love_pets', label: '🐾 Love pets' },{ value: 'neutral', label: '😐 Neutral' },{ value: 'allergic', label: '🤧 Allergic / no pets' }]} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Smoking</p>
            <PillSelect value={profile.smoking} onChange={v => set('smoking', v)}
              options={[{ value: 'non_smoker', label: '🚭 Non-smoker' },{ value: 'outdoor_only', label: '🌿 Outdoor only' },{ value: 'smoker', label: '🚬 Smoker' }]} />
          </div>
        </div>
      )}

      {/* Step 4 — Cultural */}
      {step === 4 && (
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">Values & lifestyle</h2>
          <p className="text-gray-500 mb-8">These affect daily life more than people expect. Some are dealbreakers.</p>
          <div className="space-y-5">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Dietary preference</p>
              <PillSelect value={profile.diet} onChange={v => set('diet', v)} options={[
                { value: 'no_restriction', label: '🍖 No restrictions' },{ value: 'vegetarian', label: '🥗 Vegetarian' },
                { value: 'vegan', label: '🌱 Vegan' },{ value: 'halal', label: '☪️ Halal' },
                { value: 'kosher', label: '✡️ Kosher' },
              ]} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Alcohol in the house</p>
              <PillSelect value={profile.alcohol} onChange={v => set('alcohol', v)} options={[
                { value: 'fine', label: '🍻 Totally fine' },{ value: 'prefer_not', label: '🙅 Prefer not' },{ value: 'no_alcohol', label: '🚫 No alcohol please' },
              ]} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Home language preference</p>
              <PillSelect value={profile.language_preference} onChange={v => set('language_preference', v)} options={[
                { value: 'english_only', label: '🇦🇺 English only' },{ value: 'mix', label: '🌏 Happy to mix' },{ value: 'any', label: '💬 Any language' },
              ]} />
            </div>
          </div>
        </div>
      )}

      {/* Step 5 — Vibe Quiz */}
      {step === 5 && (
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">The Vibe Quiz</h2>
          <p className="text-gray-500 mb-8">10 questions that reveal your living personality. Take your time.</p>
          <div className="space-y-6">
            {quizQuestions.map((q, qi) => (
              <div key={q.id} className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                <p className="font-semibold text-gray-900 mb-3">{qi + 1}. {q.question}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setQuizAnswers(a => ({ ...a, [qi]: oi }))}
                      className={`p-3 rounded-xl border text-left text-sm transition-all ${
                        quizAnswers[qi] === oi
                          ? 'border-[#094f6f] bg-sky-50 text-[#094f6f] font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      {opt.text}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 6 — What you're looking for + Prompts */}
      {step === 6 && (
        <div>
          <h2 className="text-3xl font-black text-gray-900 mb-2">What are you looking for?</h2>
          <p className="text-gray-500 mb-8">Almost done — tell us your situation and pick 3 prompts for your profile.</p>
          <div className="space-y-5 mb-8">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Seeking</p>
              <PillSelect value={profile.seeking} onChange={v => set('seeking', v)} options={[
                { value: 'single_housemate', label: '👤 A single housemate' },
                { value: 'group', label: '👥 2–3 people to rent with' },
                { value: 'have_property', label: '🏠 I have a property, need housemates' },
              ]} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Weekly budget: A${profile.budget_min} – A${profile.budget_max}</p>
              <div className="space-y-2">
                <input type="range" min={100} max={1000} step={25} value={profile.budget_min}
                  onChange={e => set('budget_min', Math.min(parseInt(e.target.value), profile.budget_max - 25))}
                  className="w-full accent-[#094f6f]" />
                <input type="range" min={100} max={1000} step={25} value={profile.budget_max}
                  onChange={e => set('budget_max', Math.max(parseInt(e.target.value), profile.budget_min + 25))}
                  className="w-full accent-[#094f6f]" />
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Move-in timeframe</p>
              <PillSelect value={profile.move_in_timeframe} onChange={v => set('move_in_timeframe', v)} options={[
                { value: 'asap', label: 'ASAP' },{ value: '1_month', label: '1 month' },
                { value: '2_3_months', label: '2–3 months' },{ value: 'flexible', label: 'Flexible' },
              ]} />
            </div>
          </div>
          {/* Prompts */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Pick 3 prompts for your profile</p>
            <p className="text-xs text-gray-400 mb-3">These are the first thing your matches see. ({selectedPrompts.length}/3 selected)</p>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
              {prompts.map((p, i) => {
                const isSelected = selectedPrompts.includes(i);
                return (
                  <div key={i} className="space-y-1">
                    <button onClick={() => {
                      if (isSelected) { setSelectedPrompts(sp => sp.filter(x => x !== i)); }
                      else if (selectedPrompts.length < 3) { setSelectedPrompts(sp => [...sp, i]); }
                    }}
                      className={`w-full text-left p-3 rounded-xl border text-sm transition-all ${
                        isSelected ? 'border-[#094f6f] bg-sky-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <span className={isSelected ? 'text-[#094f6f] font-medium' : 'text-gray-600'}>{p}</span>
                    </button>
                    {isSelected && (
                      <textarea value={promptAnswers[i] || ''} onChange={e => setPromptAnswers(a => ({...a, [i]: e.target.value}))}
                        placeholder="Your answer..." rows={2} maxLength={150}
                        className="w-full border border-sky-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 resize-none" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
        {step > 1 ? (
          <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors text-sm font-medium">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        ) : <div />}
        <GradBtn onClick={next} className="px-8 py-3 text-sm flex items-center gap-2">
          {step === TOTAL_STEPS ? 'Find My Matches →' : step === 5 && Object.keys(quizAnswers).length < 10 ? `Answer ${10 - Object.keys(quizAnswers).length} more` : 'Continue'} <ChevronRight className="w-4 h-4" />
        </GradBtn>
      </div>
    </div>
  );
}

// ── Swipe Feed ────────────────────────────────────────────────────────────────

function SwipeFeed({ token }) {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lastAction, setLastAction] = useState(null);
  const [matchFlash, setMatchFlash] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [remaining, setRemaining] = useState(20);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/vibemate/feed`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setFeed(d.profiles || []);
      setRemaining(d.remaining || 0);
    } catch { setFeed([]); }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const swipe = async (direction) => {
    const profile = feed[currentIdx];
    if (!profile) return;
    setLastAction(direction);
    const r = await fetch(`${API}/api/vibemate/swipe`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ swiped_id: profile.user_id, direction, target_type: 'profile' }),
    });
    const d = await r.json();
    if (d.match_created) {
      setMatchFlash(profile);
      setTimeout(() => setMatchFlash(null), 4000);
    }
    setTimeout(() => {
      setLastAction(null);
      setExpanded(false);
      setCurrentIdx(i => i + 1);
    }, 400);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-[#094f6f] border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-gray-500 text-sm">Finding your matches...</p>
    </div>
  );

  const profile = feed[currentIdx];

  if (!profile || currentIdx >= feed.length) return (
    <div className="text-center py-20">
      <div className="text-5xl mb-4">🌙</div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">You've seen everyone today</h3>
      <p className="text-gray-400 text-sm mb-6">Come back tomorrow for {remaining <= 0 ? 'new' : remaining} fresh profiles.<br/>Your daily 20 resets at midnight.</p>
      <GradBtn onClick={loadFeed} className="px-6 py-3 text-sm">Refresh</GradBtn>
    </div>
  );

  const vt = VIBE_TYPE_META[profile.vibe_type] || VIBE_TYPE_META.free_spirit;
  const compat = profile.compatibility || {};
  const tier = compat.tier || 'good';
  const ts = TIER_STYLES[tier];

  return (
    <div className="max-w-sm mx-auto">
      {/* Match flash */}
      {matchFlash && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-gradient-to-br from-[#2cbdc4] to-[#094f6f] rounded-3xl p-8 shadow-2xl text-center text-white animate-bounce">
            <div className="text-5xl mb-2">🎉</div>
            <h3 className="text-2xl font-black mb-1">It's a Match!</h3>
            <p className="text-white/80 text-sm">{matchFlash.display_name} and you both liked each other</p>
          </div>
        </div>
      )}

      {/* Card */}
      <div className={`relative rounded-3xl overflow-hidden shadow-xl transition-all duration-300 ${
        lastAction === 'right' ? 'translate-x-8 rotate-3 opacity-50' :
        lastAction === 'left'  ? '-translate-x-8 -rotate-3 opacity-50' :
        lastAction === 'super' ? 'scale-105 opacity-50' : ''
      }`}
        style={{ border: `2px solid ${ts.border}`, background: ts.bg }}>

        {/* Photo area */}
        <div className="h-64 relative overflow-hidden flex items-end justify-center"
          style={{ background: `linear-gradient(160deg,${vt.color}18 0%, white 85%)` }}>
          <img
            src={profile.photo_url || VIBE_TYPE_AVATARS[profile.vibe_type] || getVibeAvatar(profile.display_name, profile.vibe_type)}
            alt={profile.display_name}
            className={profile.photo_url
              ? 'absolute inset-0 w-full h-full object-cover'
              : 'relative w-52 h-52 object-contain select-none'}
            draggable={false}
          />
        </div>

        {/* Compat meter overlay */}
        <div className="absolute top-4 right-4">
          <CompatMeter score={compat.score || 75} size={60} />
        </div>
        <div className="absolute top-4 left-4">
          <span className="text-xs px-2 py-1 rounded-full font-semibold text-white"
            style={{ background: ts.border + 'cc' }}>{ts.label}</span>
        </div>

        {/* Info */}
        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-xl font-black text-gray-900">{profile.display_name || profile.full_name}, {profile.age}</h3>
              <p className="text-sm text-gray-500">{profile.mode === 'student' ? profile.university : profile.industry}</p>
            </div>
            <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: `${vt.color}20`, color: vt.color }}>
              {vt.emoji} {vt.label.replace('The ', '')}
            </div>
          </div>

          {/* Vibe type compat line */}
          <p className="text-xs text-gray-400 mb-4">
            Your vibes: {VIBE_TYPE_META['free_spirit']?.label?.replace('The ','')} meets {vt.label.replace('The ','')}
          </p>

          {/* Prompts */}
          {profile.prompt_answers?.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-3 mb-4 border border-gray-100">
              <p className="text-xs text-gray-400 mb-1">{profile.prompt_answers[0].question}</p>
              <p className="text-sm font-medium text-gray-800 italic">"{profile.prompt_answers[0].answer}"</p>
            </div>
          )}

          {/* Expand details */}
          {expanded && compat.dimensions && (
            <div className="space-y-2 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Compatibility breakdown</p>
              {Object.entries(compat.dimensions).map(([dim, score]) => (
                <div key={dim} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-32 capitalize">{dim.replace('_',' ')}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full" style={{ width: `${score}%`, background: 'linear-gradient(90deg,#2cbdc4,#094f6f)' }} />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-8">{score}%</span>
                </div>
              ))}
            </div>
          )}

          <button onClick={() => setExpanded(e => !e)} className="text-xs text-[#094f6f] hover:opacity-70 transition-opacity">
            {expanded ? '↑ Less details' : '↓ More details'}
          </button>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-center gap-5 mt-6">
        <button onClick={() => swipe('left')}
          className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center text-2xl border border-gray-100 hover:border-red-200 hover:bg-red-50 transition-all">
          ❌
        </button>
        <button onClick={() => swipe('super')}
          className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-xl border border-gray-100 hover:border-yellow-200 hover:bg-yellow-50 transition-all"
          title="Super Vibe (3/day)">
          ⚡
        </button>
        <button onClick={() => swipe('right')}
          className="w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center text-2xl border border-gray-100 hover:border-green-200 hover:bg-green-50 transition-all">
          💚
        </button>
      </div>

      {/* Progress */}
      <p className="text-center text-xs text-gray-400 mt-4">
        {currentIdx + 1} of {feed.length} · {remaining - currentIdx - 1} remaining today
      </p>
    </div>
  );
}

// ── Matches View ──────────────────────────────────────────────────────────────

function MatchesView({ token }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [groupModal, setGroupModal] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/vibemate/matches`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setMatches(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const createGroup = async (otherUserId) => {
    setCreating(true);
    const r = await fetch(`${API}/api/vibemate/group`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: groupName || 'Our Group', invite_user_id: otherUserId }),
    });
    setCreating(false);
    setGroupModal(null);
    setGroupName('');
    alert('🎉 Group created! You can now find a third housemate together.');
  };

  if (loading) return <div className="text-center py-20 text-gray-400">Loading matches...</div>;

  if (matches.length === 0) return (
    <div className="text-center py-20">
      <div className="text-5xl mb-4">💫</div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">No matches yet</h3>
      <p className="text-gray-400 text-sm">Start swiping to find your compatible housemates</p>
    </div>
  );

  return (
    <div>
      {groupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Start a Group 👥</h3>
            <p className="text-sm text-gray-500 mb-4">Invite {groupModal.profile.display_name} to find a third housemate together.</p>
            <input value={groupName} onChange={e => setGroupName(e.target.value)}
              placeholder="Group name (optional)" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-4 focus:outline-none" />
            <div className="flex gap-2">
              <button onClick={() => setGroupModal(null)} className="flex-1 border border-gray-200 py-2.5 rounded-xl text-sm text-gray-600">Cancel</button>
              <GradBtn onClick={() => createGroup(groupModal.other_user_id)} disabled={creating} className="flex-1 py-2.5 text-sm">
                {creating ? 'Creating...' : 'Start Group'}
              </GradBtn>
            </div>
          </div>
        </div>
      )}

      <h2 className="text-xl font-bold text-gray-900 mb-5">{matches.length} Match{matches.length !== 1 ? 'es' : ''}</h2>
      <div className="space-y-3">
        {matches.map(m => {
          const vt = VIBE_TYPE_META[m.profile?.vibe_type] || VIBE_TYPE_META.free_spirit;
          const score = m.compatibility?.score || 0;
          const tier = m.compatibility?.tier || 'good';
          return (
            <div key={m.match_id} className="bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
              style={{ borderLeft: `4px solid ${TIER_STYLES[tier].border}` }}
              onClick={() => setSelected(selected === m.match_id ? null : m.match_id)}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl overflow-hidden flex items-center justify-center flex-shrink-0"
                  style={{ background: `${vt.color}15` }}>
                  {m.profile?.photo_url
                    ? <img src={m.profile.photo_url} alt="" className="w-full h-full object-cover rounded-2xl" />
                    : <img src={VIBE_TYPE_AVATARS[m.profile?.vibe_type] || getVibeAvatar(m.profile?.display_name || m.profile?.full_name, m.profile?.vibe_type, null, 80)}
                        alt="" className="w-full h-full object-contain" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-bold text-gray-900 truncate">{m.profile?.display_name || m.profile?.full_name}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ backgroundColor: `${vt.color}20`, color: vt.color }}>{vt.emoji} {vt.label.replace('The ','')}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{m.profile?.university || m.profile?.industry}</p>
                </div>
                <CompatMeter score={score} size={48} />
              </div>
              {selected === m.match_id && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  {m.profile?.prompt_answers?.slice(0, 2).map((pa, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-1">{pa.question}</p>
                      <p className="text-sm text-gray-800 italic">"{pa.answer}"</p>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <GradBtn onClick={() => setGroupModal(m)} className="flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5">
                      <Users className="w-3.5 h-3.5" /> Start a Group
                    </GradBtn>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Groups View ───────────────────────────────────────────────────────────────

function GroupsView({ token }) {
  const [myGroups, setMyGroups] = useState([]);
  const [feedGroups, setFeedGroups] = useState([]);
  const [tab, setTab] = useState('feed');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [my, feed] = await Promise.all([
        fetch(`${API}/api/vibemate/my-groups`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/vibemate/groups/feed`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setMyGroups(Array.isArray(my) ? my : []);
      setFeedGroups(Array.isArray(feed) ? feed : []);
    } catch { /* silent */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const joinGroup = async (groupId) => {
    setJoining(groupId);
    await fetch(`${API}/api/vibemate/group/${groupId}/join`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    setJoining(null);
    load();
  };

  if (loading) return <div className="text-center py-20 text-gray-400">Loading groups...</div>;

  const GroupCard = ({ g, showJoin }) => (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-gray-900">{g.name || 'Looking for housemates'}</h3>
          <p className="text-xs text-gray-400">{g.member_count || g.members?.length || 0} members · {g.spots_left || 0} spot{g.spots_left !== 1 ? 's' : ''} left</p>
        </div>
        <span className="text-xs bg-sky-50 text-[#094f6f] px-2 py-1 rounded-full font-medium">{g.combined_budget}</span>
      </div>
      {g.bio && <p className="text-sm text-gray-600 mb-3 italic">"{g.bio}"</p>}
      <div className="flex gap-2 mb-4">
        {g.members?.slice(0, 3).map((m, i) => (
          <div key={i} className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
            style={{ background: `${VIBE_TYPE_META[m.vibe_type]?.color || '#6366F1'}15` }}>
            {m.photo_url
              ? <img src={m.photo_url} alt="" className="w-full h-full object-cover" />
              : <img src={VIBE_TYPE_AVATARS[m.vibe_type] || getVibeAvatar(m.display_name || m.full_name, m.vibe_type, null, 60)}
                  alt="" className="w-full h-full object-contain" />}
          </div>
        ))}
      </div>
      {showJoin && (g.spots_left || 0) > 0 && (
        <GradBtn onClick={() => joinGroup(g.group_id)} disabled={joining === g.group_id} className="w-full py-2.5 text-sm">
          {joining === g.group_id ? 'Joining...' : '+ Join This Group'}
        </GradBtn>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {[['feed', 'Browse Groups'], ['mine', 'My Groups']].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${tab === t ? 'bg-[#094f6f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'feed' && (
        feedGroups.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">👥</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No groups yet</h3>
            <p className="text-gray-400 text-sm">Match with someone first, then start a group together from your matches.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">{feedGroups.length} group{feedGroups.length !== 1 ? 's' : ''} looking for a housemate</p>
            {feedGroups.map(g => <GroupCard key={g.group_id} g={g} showJoin />)}
          </div>
        )
      )}

      {tab === 'mine' && (
        myGroups.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">✨</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No groups yet</h3>
            <p className="text-gray-400 text-sm">Match with someone and click "Start a Group" to begin.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {myGroups.map(g => <GroupCard key={g.group_id} g={g} showJoin={false} />)}
          </div>
        )
      )}
    </div>
  );
}

// ── Selfie uploader (VibeMate profile photo) ─────────────────────────────────

function VibeSelfieUploader({ token, currentPhoto, displayName, vibeType, savedStyle, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState('');
  const inputRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) { setError('Images only (JPG, PNG, WebP)'); return; }
    if (file.size > 10 * 1024 * 1024)             { setError('Max file size is 10 MB');        return; }
    setUploading(true); setError('');
    try {
      // 1 — Cloudinary signature
      const sig = await fetch(`${API}/api/cloudinary/signature?folder=rentalx/vibemate`).then(r => r.json());
      if (sig.detail) throw new Error(sig.detail);

      // 2 — Upload directly to Cloudinary
      const form = new FormData();
      form.append('file', file);
      form.append('api_key',   sig.api_key);
      form.append('timestamp', sig.timestamp);
      form.append('signature', sig.signature);
      form.append('folder',    sig.folder);
      const res  = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: form });
      const data = await res.json();
      if (!data.secure_url) throw new Error('Upload failed');

      // 3 — Save to VibeMate profile
      const save = await fetch(`${API}/api/vibemate/profile/photo`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_url: data.secure_url }),
      });
      if (!save.ok) throw new Error('Save failed');
      onUpload(data.secure_url);
    } catch (e) {
      setError(e.message || 'Upload failed — please try again');
    }
    setUploading(false);
  };

  const avatarSrc = currentPhoto || getVibeAvatar(displayName, vibeType, savedStyle, 160);

  return (
    <div className="flex flex-col items-center mb-6" data-testid="selfie-uploader">
      {/* Tappable avatar circle */}
      <button
        type="button"
        onClick={() => !uploading && inputRef.current?.click()}
        className="relative group mb-3 focus:outline-none"
        data-testid="selfie-upload-trigger"
        aria-label="Upload profile photo"
      >
        <div className="w-24 h-24 rounded-full overflow-hidden border-4 shadow-lg transition-all group-hover:shadow-xl"
          style={{ borderColor: currentPhoto ? '#2cbdc4' : '#e5e7eb' }}>
          {uploading ? (
            <div className="w-full h-full bg-gradient-to-br from-[#2cbdc4]/10 to-[#094f6f]/10 flex items-center justify-center">
              <Loader className="w-8 h-8 text-[#094f6f] animate-spin" />
            </div>
          ) : (
            <img
              src={avatarSrc}
              alt="Profile"
              className={`w-full h-full ${currentPhoto ? 'object-cover' : 'object-contain bg-gradient-to-b from-[#2cbdc4]/10 to-white'}`}
            />
          )}
        </div>

        {/* Camera overlay on hover */}
        <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-all">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow">
            <Camera className="w-4 h-4 text-[#094f6f]" />
          </div>
        </div>

        {/* Green verified dot when real photo exists */}
        {currentPhoto && !uploading && (
          <div className="absolute bottom-0.5 right-0.5 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center shadow">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </button>

      <button
        type="button"
        onClick={() => !uploading && inputRef.current?.click()}
        className="text-xs font-semibold text-[#094f6f] hover:opacity-70 transition-opacity"
      >
        {uploading ? 'Uploading…' : currentPhoto ? 'Change photo' : 'Add profile photo'}
      </button>

      {!currentPhoto && !uploading && (
        <p className="text-xs text-gray-400 mt-0.5 text-center">Profiles with a photo get 3× more matches</p>
      )}

      {error && (
        <p className="text-red-500 text-xs mt-2 text-center" data-testid="selfie-error">{error}</p>
      )}

      {/* Hidden file input — accept any image; on mobile shows "Take Photo / Choose from Library" */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="selfie-file-input"
        onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ''; }}
      />
    </div>
  );
}

// ── Avatar Style Picker ───────────────────────────────────────────────────────

function AvatarStylePicker({ userId, displayName, vibeType }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => getSavedAvatarStyle(userId));

  const pick = (styleId) => {
    saveAvatarStyle(userId, styleId);
    setSelected(styleId);
    setOpen(false);
  };

  if (!displayName) return null;

  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-700">Avatar Style</p>
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#094f6f] hover:opacity-70 transition-opacity"
          data-testid="avatar-style-toggle"
        >
          <RefreshCw className="w-3 h-3" />
          {open ? 'Close' : 'Change Style'}
        </button>
      </div>

      {/* Current avatar preview */}
      {!open && (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
            <img
              src={getVibeAvatar(displayName, vibeType, selected, 80)}
              alt="Your avatar"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700">
              {AVATAR_STYLE_CATALOG.find(s => s.id === (selected || ''))?.label || 'Auto-matched to your Vibe Type'}
            </p>
            <p className="text-xs text-gray-400">
              {AVATAR_STYLE_CATALOG.find(s => s.id === (selected || ''))?.tag || 'Vibe-matched style'}
            </p>
          </div>
        </div>
      )}

      {/* Style grid */}
      {open && (
        <div className="grid grid-cols-4 gap-2">
          {AVATAR_STYLE_CATALOG.map(style => {
            const isActive = (selected || null) === style.id;
            return (
              <button
                key={style.id}
                data-testid={`avatar-style-${style.id}`}
                onClick={() => pick(style.id)}
                className={`relative flex flex-col items-center gap-1 p-2 rounded-xl border transition-all ${
                  isActive
                    ? 'border-[#094f6f] bg-sky-50 ring-1 ring-[#094f6f]/30'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden">
                  <img
                    src={getVibeAvatar(displayName, vibeType, style.id, 80)}
                    alt={style.label}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <span className="text-[9px] font-semibold text-gray-600 leading-tight text-center">{style.label}</span>
                <span className="text-[8px] text-gray-400 leading-tight">{style.tag}</span>
                {isActive && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#094f6f] flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main VibeMate App ─────────────────────────────────────────────────────────

export default function VibemateApp() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('discover');
  const [hasProfile, setHasProfile] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPlus, setShowPlus] = useState(false);
  const [streak, setStreak] = useState(0);

  // Handle return from Stripe Plus payment
  const searchParams = new URLSearchParams(location.search);
  const plusSuccess = searchParams.get('plus') === 'success';
  const plusSessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!user) { navigate('/login', { state: { from: '/vibemate/app' } }); return; }

    // Load profile
    fetch(`${API}/api/vibemate/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => {
        setHasProfile(d.exists && d.profile?.completed_onboarding);
        if (d.profile) setMyProfile(d.profile);
      }).catch(() => setHasProfile(false));

    // Daily streak check-in
    fetch(`${API}/api/vibemate/streak/checkin`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => setStreak(d.streak || 0)).catch(() => {});

    // Activate Plus if returning from Stripe
    if (plusSuccess && plusSessionId && token) {
      fetch(`${API}/api/vibemate/plus/activate?session_id=${plusSessionId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        // Reload profile to get is_plus flag
        fetch(`${API}/api/vibemate/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => { if (d.profile) setMyProfile(d.profile); });
      });
    }
  }, [user, token, navigate]);

  const handleOnboardingComplete = async (profileData) => {
    setSaving(true);
    await fetch(`${API}/api/vibemate/profile`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(profileData),
    });
    setSaving(false);
    setHasProfile(true);
    setMyProfile(profileData);
  };

  if (!user) return null;
  if (hasProfile === null) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#094f6f] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const isPlus = myProfile?.is_plus || false;

  const TABS = [
    { id: 'discover',  icon: '💫', label: 'Discover'  },
    { id: 'likes',     icon: '💌', label: 'Likes'     },
    { id: 'matches',   icon: '💚', label: 'Matches'   },
    { id: 'groups',    icon: '👥', label: 'Groups'    },
    { id: 'messages',  icon: <MessageCircle className="w-4 h-4" />, label: 'Messages' },
    { id: 'profile',   icon: '👤', label: 'Profile'   },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {showPlus && <PlusUpgradeModal onClose={() => setShowPlus(false)} token={token} />}
      {plusSuccess && !showPlus && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-[#2cbdc4] to-[#094f6f] text-white px-5 py-3 rounded-full shadow-xl text-sm font-semibold flex items-center gap-2">
          <Crown className="w-4 h-4" /> VibeMate Plus activated! Welcome to the club.
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/vibemate" className="flex items-center gap-0">
            <span className="text-lg font-black" style={{
              background: 'linear-gradient(135deg,#2cbdc4,#094f6f)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>VibeMate</span>
          </Link>

          <div className="flex items-center gap-3">
            {/* Streak badge */}
            {streak > 0 && (
              <div className="flex items-center gap-1 text-xs font-bold text-orange-500">
                <Flame className="w-3.5 h-3.5" />{streak}
              </div>
            )}
            {/* Plus badge or upgrade */}
            {isPlus ? (
              <div className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full"
                style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)', color: 'white' }}>
                <Crown className="w-3 h-3" /> Plus
              </div>
            ) : (
              <button onClick={() => setShowPlus(true)}
                className="text-xs text-[#094f6f] font-semibold border border-[#094f6f]/30 px-2.5 py-1 rounded-full hover:bg-sky-50 transition-colors">
                + Plus
              </button>
            )}
            <Link to="/student-dashboard" className="text-xs text-gray-400 hover:text-gray-600">← RentalX</Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-lg mx-auto px-4 py-6">
        {!hasProfile ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            {saving ? (
              <div className="text-center py-10">
                <div className="w-10 h-10 border-4 border-[#094f6f] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Setting up your VibeMate profile...</p>
              </div>
            ) : (
              <Onboarding onComplete={handleOnboardingComplete} />
            )}
          </div>
        ) : (
          <>
            {activeTab === 'discover'  && <SwipeFeed token={token} isPlus={isPlus} />}
            {activeTab === 'likes'     && <LikesView token={token} isPlus={isPlus} onUpgrade={() => setShowPlus(true)} />}
            {activeTab === 'matches'   && <MatchesView token={token} />}
            {activeTab === 'groups'    && <GroupsView token={token} />}
            {activeTab === 'messages'  && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-5">Messages</h2>
                <MessagingSystem userType="student" />
              </div>
            )}
            {activeTab === 'profile'   && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">My VibeMate Profile</h2>

                  {/* Selfie upload + avatar */}
                  {(() => {
                    const savedStyle = getSavedAvatarStyle(user.id);
                    return (
                      <VibeSelfieUploader
                        token={token}
                        currentPhoto={myProfile?.photo_url || ''}
                        displayName={myProfile?.display_name}
                        vibeType={myProfile?.vibe_type}
                        savedStyle={savedStyle}
                        onUpload={url => setMyProfile(p => ({ ...p, photo_url: url }))}
                      />
                    );
                  })()}

                  {/* Vibe type label */}
                  {myProfile?.vibe_type && (() => {
                    const vt = VIBE_TYPE_META[myProfile.vibe_type];
                    const vtAvatar = VIBE_TYPE_AVATARS[myProfile.vibe_type];
                    return (
                      <div className="flex items-center justify-center gap-2 mb-5">
                        {vtAvatar
                          ? <img src={vtAvatar} alt={vt?.label} className="w-6 h-6 rounded-md object-cover" />
                          : <span className="text-lg">{vt?.emoji}</span>
                        }
                        <p className="text-sm font-bold text-gray-700">{vt?.label}</p>
                      </div>
                    );
                  })()}

                  {/* Avatar style picker — only when no real photo */}
                  {!myProfile?.photo_url && (
                    <AvatarStylePicker userId={user.id} displayName={myProfile?.display_name} vibeType={myProfile?.vibe_type} />
                  )}

                  {/* Profile completeness */}
                  {(() => {
                    const fields = ['display_name','age','photo_url','vibe_type','prompt_answers','preferred_suburbs'];
                    const done = fields.filter(f => {
                      const v = myProfile?.[f];
                      return v && (Array.isArray(v) ? v.length > 0 : true);
                    }).length;
                    const pct = Math.round((done / fields.length) * 100);
                    return pct < 100 ? (
                      <div className="mb-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="font-medium text-amber-800">Profile completeness</span>
                          <span className="font-bold text-amber-800">{pct}%</span>
                        </div>
                        <div className="w-full bg-amber-100 rounded-full h-2">
                          <div className="h-2 rounded-full bg-amber-500" style={{ width: `${pct}%` }} />
                        </div>
                        {!myProfile?.photo_url && (
                          <p className="text-xs text-amber-600 mt-2">Tip: Upload a real photo to get 3x more matches</p>
                        )}
                      </div>
                    ) : null;
                  })()}

                  {/* Streak */}
                  {streak > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-xl border border-orange-100 mb-4">
                      <Flame className="w-5 h-5 text-orange-500" />
                      <div>
                        <p className="font-bold text-orange-800 text-sm">{streak}-day streak! 🔥</p>
                        <p className="text-xs text-orange-500">{streak >= 7 ? '⭐ Hot Profile badge active for 48h' : `${7 - streak} more days for Hot Profile badge`}</p>
                      </div>
                    </div>
                  )}

                  <GradBtn onClick={() => setHasProfile(false)} className="w-full py-3 text-sm">
                    Edit My Profile
                  </GradBtn>
                </div>

                {/* University verification */}
                <UniVerification token={token} profile={myProfile}
                  onVerified={() => setMyProfile(p => ({ ...p, university_email_verified: true }))} />

                {/* Plus info */}
                {!isPlus && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Crown className="w-5 h-5 text-[#094f6f]" />
                        <p className="font-bold text-gray-900">VibeMate Plus</p>
                      </div>
                      <span className="text-sm font-black text-[#094f6f]">A$9.99/mo</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Unlimited profiles, see who liked you, advanced filters, boost your profile.</p>
                    <GradBtn onClick={() => setShowPlus(true)} className="w-full py-2.5 text-sm flex items-center justify-center gap-2">
                      <Crown className="w-3.5 h-3.5" /> Upgrade to Plus
                    </GradBtn>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom navigation */}
      {hasProfile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg z-30">
          <div className="max-w-lg mx-auto flex">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center py-2.5 gap-0.5 transition-colors ${activeTab === tab.id ? 'text-[#094f6f]' : 'text-gray-400 hover:text-gray-600'}`}>
                <span className="text-lg">{tab.icon}</span>
                <span className="text-[9px] font-semibold">{tab.label}</span>
                {activeTab === tab.id && <div className="w-1 h-1 rounded-full bg-[#094f6f] mt-0.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
